import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    MessageSquare, 
    Briefcase, 
    FileText, 
    Users, 
    Zap, 
    TrendingUp, 
    Clock, 
    DollarSign,
    AlertCircle,
    Activity,
    CheckCircle,
    XCircle,
    ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { AdminLayout } from './components/AdminLayout';
import { AdminTopBar } from './components/AdminTopBar';
import { StatCard } from './components/StatCard';
import { applicationStorage } from '../../utils/applicationStorage';
import { resumeStorage } from '../../utils/resumeStorage';
import { enquiryStorage } from '../../utils/enquiryStorage';
import { userStorage } from '../../utils/userStorage';
import { useJobs } from '../../contexts/JobsContext';
import { applicationsAPI, resumesAPI, enquiriesAPI } from '../../../services/api';

export function AdminDashboard() {
    const navigate = useNavigate();
    const { jobs } = useJobs();
    const [isLoading, setIsLoading] = useState(true);
    const [applicationCount, setApplicationCount] = useState(0);
    const [newApplicationCount, setNewApplicationCount] = useState(0);
    const [resumeCount, setResumeCount] = useState(0);
    const [newResumeCount, setNewResumeCount] = useState(0);
    const [enquiryCount, setEnquiryCount] = useState(0);
    const [newEnquiryCount, setNewEnquiryCount] = useState(0);
    const [userCount, setUserCount] = useState(0);
    const [newUserCount, setNewUserCount] = useState(0);
    
    // Store full lists for status breakdowns
    const [applications, setApplications] = useState<any[]>([]);
    const [resumes, setResumes] = useState<any[]>([]);
    const [enquiries, setEnquiries] = useState<any[]>([]);

    useEffect(() => {
        const adminAuth = localStorage.getItem('adminAuth');
        if (!adminAuth) {
            navigate('/admin');
        }

        // Fetch data from APIs
        const fetchData = async () => {
            setIsLoading(true);
            try {
                // Get application counts from API
                const applicationsRes = await applicationsAPI.getAll({ page: 1, limit: 1000 });
                const applicationsList = applicationsRes.data || applicationsRes.applications || applicationsRes.items || [];
                setApplications(applicationsList); // Store full list
                setApplicationCount(applicationsList.length);
                setNewApplicationCount(applicationsList.filter((app: any) => app.status === 'new').length);

                // Get resume counts from API
                const resumesRes = await resumesAPI.getAll({ page: 1, limit: 1000 });
                const resumesList = resumesRes.data || resumesRes.resumes || resumesRes.items || [];
                setResumes(resumesList); // Store full list
                setResumeCount(resumesList.length);
                setNewResumeCount(resumesList.filter((resume: any) => resume.status === 'new').length);

                // Get enquiry counts from API
                const enquiriesRes = await enquiriesAPI.getAll({ page: 1, limit: 1000 });
                const enquiriesList = enquiriesRes.data || enquiriesRes.enquiries || enquiriesRes.items || [];
                setEnquiries(enquiriesList); // Store full list
                setEnquiryCount(enquiriesList.length);
                setNewEnquiryCount(enquiriesList.filter((enquiry: any) => enquiry.status === 'new').length);
            } catch (error) {
                console.error('Error fetching dashboard data from API:', error);
                toast.error('Failed to load dashboard data from database');
                setApplications([]);
                setApplicationCount(0);
                setNewApplicationCount(0);
                setResumes([]);
                setResumeCount(0);
                setNewResumeCount(0);
                setEnquiries([]);
                setEnquiryCount(0);
                setNewEnquiryCount(0);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();

        // Get user counts from localStorage (users are still local only)
        const users = userStorage.getAll();
        setUserCount(users.length);
        // Count recently created users (created within last 7 days) as "new"
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        setNewUserCount(users.filter(user => {
            const createdDate = new Date(user.createdDate).getTime();
            return createdDate > sevenDaysAgo;
        }).length);
    }, [navigate]);

    // Calculate active jobs
    const activeJobsCount = jobs.filter(job => job.status === 'active').length;
    const totalJobsCount = jobs.length;

    // Get active users count (users with 'Active' status)
    const activeUsers = userStorage.getAll().filter(user => user.status === 'Active');
    const activeUserCount = activeUsers.length;

    // Get application status breakdown (using state data from MongoDB)
    const applicationStatusBreakdown = [
        { 
            label: 'New', 
            count: applications.filter(app => app.status === 'new').length, 
            icon: AlertCircle, 
            color: 'text-blue-400', 
            bgColor: 'bg-blue-400/10',
            borderColor: 'border-blue-500/30'
        },
        { 
            label: 'Reviewing', 
            count: applications.filter(app => app.status === 'reviewing').length, 
            icon: Activity, 
            color: 'text-purple-400', 
            bgColor: 'bg-purple-400/10',
            borderColor: 'border-purple-500/30'
        },
        { 
            label: 'Shortlisted', 
            count: applications.filter(app => app.status === 'shortlisted').length, 
            icon: CheckCircle, 
            color: 'text-yellow-400', 
            bgColor: 'bg-yellow-400/10',
            borderColor: 'border-yellow-500/30'
        },
        { 
            label: 'Interviewed', 
            count: applications.filter(app => app.status === 'interviewed').length, 
            icon: Users, 
            color: 'text-cyan-400', 
            bgColor: 'bg-cyan-400/10',
            borderColor: 'border-cyan-500/30'
        },
        { 
            label: 'Rejected', 
            count: applications.filter(app => app.status === 'rejected').length, 
            icon: XCircle, 
            color: 'text-red-400', 
            bgColor: 'bg-red-400/10',
            borderColor: 'border-red-500/30'
        },
        { 
            label: 'Hired', 
            count: applications.filter(app => app.status === 'hired').length, 
            icon: CheckCircle, 
            color: 'text-green-400', 
            bgColor: 'bg-green-400/10',
            borderColor: 'border-green-500/30'
        }
    ];

    const stats = [
        { 
            icon: MessageSquare, 
            label: 'Total Enquiries', 
            value: isLoading ? null : enquiryCount.toString(), 
            change: isLoading ? null : (newEnquiryCount > 0 ? `${newEnquiryCount} new` : 'No new'),
            changeType: 'positive' as const,
            iconColor: 'text-cyan-400',
            iconBgColor: 'bg-cyan-400/10'
        },
        { 
            icon: Briefcase, 
            label: 'Total Applications', 
            value: isLoading ? null : applicationCount.toString(), 
            change: isLoading ? null : (newApplicationCount > 0 ? `${newApplicationCount} new` : 'No new'),
            changeType: 'positive' as const,
            iconColor: 'text-purple-400',
            iconBgColor: 'bg-purple-400/10'
        },
        { 
            icon: FileText, 
            label: 'Total Resumes', 
            value: isLoading ? null : resumeCount.toString(), 
            change: isLoading ? null : (newResumeCount > 0 ? `${newResumeCount} new` : 'No new'),
            changeType: 'positive' as const,
            iconColor: 'text-green-400',
            iconBgColor: 'bg-green-400/10'
        },
        { 
            icon: Zap, 
            label: 'Active Jobs', 
            value: isLoading ? null : activeJobsCount.toString(), 
            change: isLoading ? null : `${totalJobsCount} total`,
            changeType: 'positive' as const,
            iconColor: 'text-orange-400',
            iconBgColor: 'bg-orange-400/10'
        }
    ];

    const quickStats = [
        { label: 'Conversion Rate', value: '3.2%', icon: TrendingUp, color: 'text-green-400' },
        { label: 'Avg Response Time', value: '2.4h', icon: Clock, color: 'text-blue-400' },
        { label: 'Active Jobs', value: activeJobsCount.toString(), icon: Briefcase, color: 'text-purple-400' },
        { label: 'Revenue', value: '$12.5K', icon: DollarSign, color: 'text-cyan-400' }
    ];

    const recentActivity = [
        { 
            type: 'enquiry',
            icon: MessageSquare, 
            title: 'New enquiry from Tech Corp Inc.', 
            description: 'Interested in AI Solutions',
            time: '5 min ago',
            color: 'text-cyan-400',
            bgColor: 'bg-cyan-400/10'
        },
        { 
            type: 'application',
            icon: Briefcase, 
            title: 'Application for Senior AI Engineer', 
            description: 'Alice Johnson applied',
            time: '15 min ago',
            color: 'text-purple-400',
            bgColor: 'bg-purple-400/10'
        },
        { 
            type: 'resume',
            icon: FileText, 
            title: 'Resume submitted', 
            description: 'Emma Wilson - Data Scientist',
            time: '1 hour ago',
            color: 'text-green-400',
            bgColor: 'bg-green-400/10'
        },
        { 
            type: 'user',
            icon: Users, 
            title: 'New user registration', 
            description: 'john.doe@example.com',
            time: '2 hours ago',
            color: 'text-orange-400',
            bgColor: 'bg-orange-400/10'
        },
        { 
            type: 'enquiry',
            icon: MessageSquare, 
            title: 'Enquiry marked as replied', 
            description: 'Healthcare Systems inquiry',
            time: '3 hours ago',
            color: 'text-cyan-400',
            bgColor: 'bg-cyan-400/10'
        }
    ];

    const statusData = [
        { label: 'Pending Review', count: 45, icon: AlertCircle, color: 'text-yellow-400', bgColor: 'bg-yellow-400/10' },
        { label: 'In Progress', count: 28, icon: Activity, color: 'text-blue-400', bgColor: 'bg-blue-400/10' },
        { label: 'Completed', count: 156, icon: CheckCircle, color: 'text-green-400', bgColor: 'bg-green-400/10' },
        { label: 'Rejected', count: 12, icon: XCircle, color: 'text-red-400', bgColor: 'bg-red-400/10' }
    ];

    const quickActions = [
        { 
            label: 'Manage Enquiries', 
            path: '/admin/enquiries', 
            icon: MessageSquare, 
            count: isLoading ? null : (newEnquiryCount > 0 ? `${newEnquiryCount} new` : 'No new'),
            color: 'from-cyan-500/20 to-blue-500/20',
            borderColor: 'border-cyan-500/30'
        },
        { 
            label: 'Review Applications', 
            path: '/admin/applications', 
            icon: Briefcase, 
            count: isLoading ? null : (newApplicationCount > 0 ? `${newApplicationCount} new` : 'No new'),
            color: 'from-purple-500/20 to-pink-500/20',
            borderColor: 'border-purple-500/30'
        },
        { 
            label: 'View Resumes', 
            path: '/admin/resumes', 
            icon: FileText, 
            count: isLoading ? null : (newResumeCount > 0 ? `${newResumeCount} new` : 'No new'),
            color: 'from-green-500/20 to-teal-500/20',
            borderColor: 'border-green-500/30'
        },
        { 
            label: 'Manage Careers', 
            path: '/admin/careers', 
            icon: Zap, 
            count: isLoading ? null : `${totalJobsCount} total`,
            color: 'from-orange-500/20 to-yellow-500/20',
            borderColor: 'border-orange-500/30'
        }
    ];

    return (
        <AdminLayout>
            <AdminTopBar
                title="Dashboard Overview"
                description="Monitor and manage your admin operations"
            />

            <div className="p-8 space-y-6">
                {/* Main Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((stat, index) => {
                        const routes = [
                            '/admin/enquiries',     // Total Enquiries
                            '/admin/applications',  // Total Applications
                            '/admin/resumes',       // Total Resumes
                            '/admin/careers'        // Active Jobs
                        ];
                        
                        return (
                            <Link 
                                key={index} 
                                to={routes[index]}
                                className="block transition-transform hover:scale-105"
                            >
                                <StatCard
                                    icon={stat.icon}
                                    title={stat.label}
                                    value={stat.value}
                                    change={stat.change}
                                    changeType={stat.changeType}
                                    iconColor={stat.iconColor}
                                    iconBgColor={stat.iconBgColor}
                                />
                            </Link>
                        );
                    })}
                </div>

                {/* Job Applications Status Breakdown */}
                <div className="glass-strong rounded-xl border border-primary/30 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-foreground font-bold text-lg">Job Applications Status</h3>
                        <Link 
                            to="/admin/applications"
                            className="text-sm text-primary hover:text-primary/80 transition-all duration-300 font-semibold hover:scale-105 flex items-center gap-1"
                        >
                            View All
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {applicationStatusBreakdown.map((status, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ scale: 1.05, y: -4 }}
                                className={`glass-strong rounded-xl border ${status.borderColor} p-4 cursor-pointer hover:shadow-lg transition-all duration-300`}
                            >
                                <div className={`p-2 rounded-lg ${status.bgColor} border border-primary/20 w-fit mb-3`}>
                                    <status.icon className={`w-5 h-5 ${status.color}`} />
                                </div>
                                <p className="text-sm text-muted-foreground mb-1">{status.label}</p>
                                {isLoading ? (
                                    <div className="h-8 w-12 bg-muted/20 animate-pulse rounded"></div>
                                ) : (
                                    <p className={`text-2xl ${status.color}`}>{status.count}</p>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Additional Stats & Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Quick Stats */}
                    <div className="glass-strong rounded-xl border border-primary/30 p-6">
                        <h3 className="text-foreground mb-4 font-bold text-lg">Performance Metrics</h3>
                        <div className="space-y-3">
                            {quickStats.map((stat, index) => (
                                <motion.div 
                                    key={index} 
                                    whileHover={{ scale: 1.02 }}
                                    className="flex items-center justify-between p-3 rounded-lg glass hover:border-primary/30 transition-all duration-300 cursor-pointer"
                                >
                                    <div className="flex items-center gap-3">
                                        <stat.icon className={`w-5 h-5 ${stat.color}`} />
                                        <span className="text-sm text-muted-foreground font-medium">{stat.label}</span>
                                    </div>
                                    {isLoading ? (
                                        <div className="h-6 w-16 bg-muted/20 animate-pulse rounded"></div>
                                    ) : (
                                        <span className="text-foreground font-bold">{stat.value}</span>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Status Overview */}
                    <div className="glass-strong rounded-xl border border-primary/30 p-6">
                        <h3 className="text-foreground mb-4 font-bold text-lg">Status Overview</h3>
                        <div className="space-y-3">
                            {statusData.map((status, index) => (
                                <motion.div 
                                    key={index} 
                                    whileHover={{ scale: 1.02 }}
                                    className="flex items-center justify-between p-3 rounded-lg glass hover:border-primary/30 transition-all duration-300"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${status.bgColor} border border-primary/20`}>
                                            <status.icon className={`w-4 h-4 ${status.color}`} />
                                        </div>
                                        <span className="text-sm text-foreground font-medium">{status.label}</span>
                                    </div>
                                    {isLoading ? (
                                        <div className="h-7 w-12 bg-muted/20 animate-pulse rounded-full"></div>
                                    ) : (
                                        <span className={`text-foreground font-bold px-3 py-1 rounded-full ${status.bgColor} border border-primary/20`}>
                                            {status.count}
                                        </span>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="glass-strong rounded-xl border border-primary/30 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-foreground font-bold text-lg">Recent Activity</h3>
                            <button className="text-sm text-primary hover:text-primary/80 transition-all duration-300 font-semibold hover:scale-105">
                                View All
                            </button>
                        </div>
                        <div className="space-y-3">
                            {recentActivity.map((activity, index) => {
                                const IconComponent = activity.icon;
                                return (
                                    <motion.div
                                        key={index}
                                        whileHover={{ x: 4, scale: 1.02 }}
                                        className="flex items-start gap-3 p-3 rounded-lg glass hover:border-primary/30 transition-all duration-300 cursor-pointer"
                                    >
                                        <div className={`p-2 rounded-lg ${activity.bgColor} border border-primary/20 mt-1 shrink-0`}>
                                            <IconComponent className={`w-4 h-4 ${activity.color}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-foreground truncate font-medium">{activity.title}</p>
                                            <p className="text-xs text-muted-foreground truncate">{activity.description}</p>
                                            <p className="text-xs text-muted-foreground mt-1">{activity.time}</p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* System Health */}
                <div className="glass-strong rounded-xl border border-primary/30 p-6">
                    <h3 className="text-foreground mb-4">System Health</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="p-4 rounded-lg glass border border-green-500/30">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-muted-foreground">Database</span>
                                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                            </div>
                            <p className="text-foreground">Online</p>
                            <p className="text-xs text-green-400 mt-1">99.9% uptime</p>
                        </div>
                        <div className="p-4 rounded-lg glass border border-green-500/30">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-muted-foreground">API</span>
                                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                            </div>
                            <p className="text-foreground">Healthy</p>
                            <p className="text-xs text-green-400 mt-1">45ms avg</p>
                        </div>
                        <div className="p-4 rounded-lg glass border border-yellow-500/30">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-muted-foreground">Storage</span>
                                <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></div>
                            </div>
                            <p className="text-foreground">Warning</p>
                            <p className="text-xs text-yellow-400 mt-1">78% used</p>
                        </div>
                        <div className="p-4 rounded-lg glass border border-green-500/30">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-muted-foreground">CDN</span>
                                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                            </div>
                            <p className="text-foreground">Optimal</p>
                            <p className="text-xs text-green-400 mt-1">Global</p>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
